import React, { useState, useEffect, } from 'react';
// import { useNavigate } from 'react-router-dom';
import api from '../services/api';

// For accessible label pairing
const choiceLabels = ['A', 'B', 'C', 'D', 'E'];

function QuestionAddPage() {
  // const navigate = useNavigate();

  // Live categories and states
  const [categories, setCategories] = useState([]);
  const [states, setStates] = useState([]);

  // Form state
  const [question, setQuestion] = useState({
    question_text: '',
    choices: ['', '', '', '', ''],
    correct_choice: '',
    rationale: '',
    category_id: '',
    state_id: '', // or multi-state support
    difficulty: '',
    tags: '',
    comment: '',
    media_url: '',
    status: 'draft', // or 'pending'
  });
  const [file, setFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Bulk upload
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkStatus, setBulkStatus] = useState('');

  // Fetch categories/states on mount
  useEffect(() => {
    (async () => {
      try {
        const [catRes, stateRes] = await Promise.all([
          api.get('/categories'),
          api.get('/states/user-status'),
        ]);
        setCategories(catRes.data || []);
        setStates(stateRes.data || []);
      } catch (err) {
        setError('Failed to load category or state data.');
      }
    })();
  }, []);

  // Media upload preview
  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setMediaPreview(ev.target.result);
    reader.readAsDataURL(f);
  };

  // Field handler (general)
  const handleChange = (e) => {
    const { name, value } = e.target;
    setQuestion(prev => ({ ...prev, [name]: value }));
  };

  // Choices (array, 0=A, 1=B, ...)
  const handleChoiceChange = (idx, value) => {
    setQuestion(prev => {
      const newChoices = [...prev.choices];
      newChoices[idx] = value;
      return { ...prev, choices: newChoices };
    });
  };

  // Bulk upload (CSV/XLSX)
  const handleBulkFileChange = (e) => setBulkFile(e.target.files[0]);
  const handleBulkUpload = async () => {
    if (!bulkFile) return;
    setBulkStatus('Uploading...');
    const formData = new FormData();
    formData.append('file', bulkFile);

    try {
      const res = await api.post('/admin/questions/bulk', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Backend should return summary, including duplicates detected, successful, rejected, etc.
      setBulkStatus(`Bulk upload complete: ${res.data.summary || JSON.stringify(res.data)}`);
    } catch (err) {
      setBulkStatus(`Bulk upload failed: ${err.response?.data?.message || err.message}`);
    }
  };

  // Save (single question)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      let mediaUrl = '';
      if (file) {
        // Upload media first, get URL
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/admin/media-upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        mediaUrl = res.data.url;
      }
      const payload = {
        ...question,
        choices: question.choices, // array, backend must support this
        media_url: mediaUrl || question.media_url,
        status: 'pending', // force new submissions to pending
      };
      await api.post('/admin/questions', payload);
      setSuccess('Question submitted! It will be reviewed before appearing in quizzes.');
      setQuestion({
        question_text: '',
        choices: ['', '', '', '', ''],
        correct_choice: '',
        rationale: '',
        category_id: '',
        state_id: '',
        difficulty: '',
        tags: '',
        comment: '',
        media_url: '',
        status: 'draft',
      });
      setFile(null);
      setMediaPreview('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit question.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <h1>Add New Question</h1>
      <form className="question-form" onSubmit={handleSubmit} aria-label="Add new question form">
        {/* Question text */}
        <div className="form-group">
          <label htmlFor="question_text">Question Text</label>
          <textarea
            id="question_text"
            name="question_text"
            value={question.question_text}
            onChange={handleChange}
            rows="3"
            required
          />
        </div>

        {/* Choices */}
        <div className="form-group">
          <label>Choices</label>
          {choiceLabels.map((label, idx) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="radio"
                name="correct_choice"
                id={`correct_${label}`}
                value={label}
                checked={question.correct_choice === label}
                onChange={handleChange}
                required={idx === 0}
                aria-label={`Set ${label} as correct answer`}
              />
              <input
                type="text"
                value={question.choices[idx]}
                onChange={e => handleChoiceChange(idx, e.target.value)}
                placeholder={`Choice ${label}`}
                required={idx < 4}
                aria-label={`Choice ${label}`}
              />
              <label htmlFor={`correct_${label}`} style={{ margin: 0, fontWeight: 400 }}>
                {label} {idx === 4 && <span style={{ color: "#888" }}>(Optional)</span>}
              </label>
            </div>
          ))}
        </div>

        {/* Category dropdown */}
        <div className="form-group">
          <label htmlFor="category_id">Category</label>
          <select
            id="category_id"
            name="category_id"
            value={question.category_id}
            onChange={handleChange}
            required
          >
            <option value="">--Select Category--</option>
            {categories.map(cat => (
              <option key={cat.category_id} value={cat.category_id}>
                {cat.category_name}
              </option>
            ))}
          </select>
        </div>

        {/* State dropdown */}
        <div className="form-group">
          <label htmlFor="state_id">State</label>
          <select
            id="state_id"
            name="state_id"
            value={question.state_id}
            onChange={handleChange}
            required
          >
            <option value="">--Select State--</option>
            {states.map(state => (
              <option key={state.state_id} value={state.state_id}>
                {state.state_abbr} - {state.state_name}
              </option>
            ))}
          </select>
        </div>

        {/* Difficulty */}
        <div className="form-group">
          <label htmlFor="difficulty">Difficulty</label>
          <select
            id="difficulty"
            name="difficulty"
            value={question.difficulty}
            onChange={handleChange}
          >
            <option value="">Select difficulty</option>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>
        </div>

        {/* Tags */}
        <div className="form-group">
          <label htmlFor="tags">Tags <span style={{ color: "#888" }}>(comma separated)</span></label>
          <input
            id="tags"
            name="tags"
            value={question.tags}
            onChange={handleChange}
            placeholder="e.g. insurance, policy, claim"
          />
        </div>

        {/* Media upload */}
        <div className="form-group">
          <label htmlFor="media">Attach Media (image/audio/video)</label>
          <input
            id="media"
            type="file"
            accept="image/*,audio/*,video/*"
            onChange={handleFileChange}
          />
          {mediaPreview && (
            <div className="media-preview">
              {mediaPreview.startsWith('data:image') && (
                <img src={mediaPreview} alt="Preview" style={{ maxWidth: 200, borderRadius: 8 }} />
              )}
              {mediaPreview.startsWith('data:audio') && (
                <audio controls src={mediaPreview} />
              )}
              {mediaPreview.startsWith('data:video') && (
                <video controls src={mediaPreview} style={{ maxWidth: 200, borderRadius: 8 }} />
              )}
            </div>
          )}
        </div>

        {/* Rationale */}
        <div className="form-group">
          <label htmlFor="rationale">Rationale</label>
          <textarea
            id="rationale"
            name="rationale"
            value={question.rationale}
            onChange={handleChange}
            rows="3"
            required
            placeholder="Explain why the answer is correct."
          />
          <small>
            Tip: Explain why the correct answer is best, and why wrong choices are not.
          </small>
        </div>

        {/* Comment (to admin/reviewer) */}
        <div className="form-group">
          <label htmlFor="comment">Comment to Reviewer (Optional)</label>
          <textarea
            id="comment"
            name="comment"
            value={question.comment}
            onChange={handleChange}
            rows="2"
            placeholder="Anything you want the reviewer to know?"
          />
        </div>

        {/* Status (hidden) */}
        <input type="hidden" name="status" value={question.status} />

        {/* Submit/Save */}
        <div className="form-actions">
          <button type="submit" className="action-button" disabled={loading}>
            {loading ? 'Submitting...' : 'Submit for Review'}
          </button>
        </div>
        {success && <div className="success-message">{success}</div>}
        {error && <div className="error-message">{error}</div>}
      </form>

      {/* ---- BULK UPLOAD ---- */}
      <div className="bulk-upload-section">
        <h3>Bulk Upload Questions</h3>
        <input
          type="file"
          accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
          onChange={handleBulkFileChange}
        />
        <button
          onClick={handleBulkUpload}
          className="action-button"
          disabled={!bulkFile}
          style={{ marginTop: 8 }}
        >
          Upload File
        </button>
        {bulkStatus && <div className="bulk-status">{bulkStatus}</div>}
        <small>
          CSV/XLSX format required. Duplicates will be flagged and NOT added automatically.
        </small>
      </div>
    </div>
  );
}

export default QuestionAddPage;
